import React, { useEffect, useState } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Gift } from "lucide-react";

export default function AuthPage() {
  const { user, login, signup } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialRef = searchParams.get("ref") || "";
  const [mode, setMode] = useState(initialRef ? "signup" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "", ref_code: initialRef });
  const [loading, setLoading] = useState(false);
  const [referrer, setReferrer] = useState(null); // { first_name, bonus_days }

  useEffect(() => {
    if (!initialRef) return;
    api.get(`/referrals/lookup/${initialRef}`)
      .then((r) => setReferrer(r.data))
      .catch(() => { /* invalid code, ignore silently */ });
  }, [initialRef]);

  if (user) return <Navigate to="/app" replace />;

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Welcome back. Engine warmed up.");
      } else {
        if (!form.name.trim()) throw new Error("Name is required");
        await signup(form.name, form.email, form.password, form.ref_code || undefined);
        toast.success(form.ref_code ? "Account live. Referral applied." : "Account live. Let's go viral.");
      }
      navigate("/app");
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Auth failed";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-void">
      {/* Left cover */}
      <div
        className="hidden md:flex relative bg-cover bg-center"
        style={{
          backgroundImage:
            "linear-gradient(to bottom right, rgba(0,0,0,0.65), rgba(0,0,0,0.85)), url('https://images.unsplash.com/photo-1526289034009-0240ddb68ce3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBnZW9tZXRyeXxlbnwwfHx8fDE3ODExMDM5MjV8MA&ixlib=rb-4.1.0&q=85')",
        }}
      >
        <div className="absolute inset-0 flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-signal-yellow text-black flex items-center justify-center font-mono font-bold">V</div>
            <div>
              <div className="heading-display text-lg leading-none">VIRAL/CO</div>
              <div className="font-mono text-[10px] text-zinc-400 tracking-[0.25em] mt-1">CONTENT MACHINE</div>
            </div>
          </div>
          <div>
            <h1 className="heading-display text-5xl lg:text-7xl text-white">
              VIRAL CONTENT.<br />
              <span className="text-signal-yellow">NO EXCUSES.</span>
            </h1>
            <p className="mt-6 font-mono text-xs text-zinc-400 tracking-wider max-w-md uppercase">
              // hook forge · remix lab · score engine — all engineered by Claude Sonnet 4.5.
            </p>
          </div>
          <div className="font-mono text-[10px] text-zinc-600 tracking-[0.25em] uppercase">
            v1.0 // ops_node_07 // build 2026.02
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <form onSubmit={onSubmit} className="w-full max-w-md space-y-6" data-testid="auth-form">
          {referrer && (
            <div
              data-testid="referral-banner"
              className="border-2 border-signal-yellow bg-signal-yellow/5 p-4 flex items-start gap-3"
            >
              <Gift size={20} className="text-signal-yellow shrink-0 mt-0.5" />
              <div className="space-y-1">
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-signal-yellow">// invitation</div>
                <div className="heading-display text-lg leading-tight">
                  <span className="text-signal-yellow">{referrer.first_name}</span> sent you<br />
                  {referrer.bonus_days} days of Pro free.
                </div>
                <p className="font-mono text-[10px] text-zinc-400 mt-2">
                  // sign up + upgrade to claim. both of you win.
                </p>
              </div>
            </div>
          )}
          <div className="space-y-2">
            <div className="label-eyebrow">{mode === "login" ? "Sign in" : "Create account"}</div>
            <h2 className="heading-display text-4xl">{mode === "login" ? "ACCESS GRANTED." : "JOIN THE LAB."}</h2>
            <p className="font-mono text-xs text-zinc-500">
              {mode === "login"
                ? "Punch in. Generate. Dominate."
                : "Free signup. No credit card. Just bring the chaos."}
            </p>
          </div>

          {mode === "signup" && (
            <label className="block space-y-2">
              <span className="label-eyebrow">Name</span>
              <input
                data-testid="auth-name"
                type="text"
                value={form.name}
                onChange={onChange("name")}
                placeholder="Jane Creator"
                className="w-full bg-[#141414] border border-white/10 text-white placeholder:text-zinc-600 focus:border-signal-yellow focus:outline-none focus:ring-1 focus:ring-signal-yellow p-3 font-mono text-sm"
                required
              />
            </label>
          )}

          {mode === "signup" && (
            <label className="block space-y-2">
              <span className="label-eyebrow">Referral code <span className="text-zinc-600 normal-case tracking-normal">(optional — get 30 days free)</span></span>
              <input
                data-testid="auth-refcode"
                type="text"
                value={form.ref_code}
                onChange={onChange("ref_code")}
                placeholder="e.g. abc123xy"
                className="w-full bg-[#141414] border border-white/10 text-white placeholder:text-zinc-600 focus:border-signal-yellow focus:outline-none focus:ring-1 focus:ring-signal-yellow p-3 font-mono text-sm uppercase"
              />
            </label>
          )}

          <label className="block space-y-2">
            <span className="label-eyebrow">Email</span>
            <input
              data-testid="auth-email"
              type="email"
              value={form.email}
              onChange={onChange("email")}
              placeholder="you@domain.com"
              className="w-full bg-[#141414] border border-white/10 text-white placeholder:text-zinc-600 focus:border-signal-yellow focus:outline-none focus:ring-1 focus:ring-signal-yellow p-3 font-mono text-sm"
              required
            />
          </label>

          <label className="block space-y-2">
            <span className="label-eyebrow">Password</span>
            <input
              data-testid="auth-password"
              type="password"
              value={form.password}
              onChange={onChange("password")}
              placeholder="min. 6 characters"
              className="w-full bg-[#141414] border border-white/10 text-white placeholder:text-zinc-600 focus:border-signal-yellow focus:outline-none focus:ring-1 focus:ring-signal-yellow p-3 font-mono text-sm"
              required
              minLength={6}
            />
          </label>

          <button
            data-testid="auth-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-signal-yellow text-black font-bold uppercase tracking-[0.18em] text-xs px-6 py-4 hover:bg-signal-yellow-hover transition-colors disabled:opacity-40"
          >
            {loading ? "Processing..." : mode === "login" ? "Sign in →" : "Create account →"}
          </button>

          <button
            data-testid="auth-toggle"
            type="button"
            onClick={() => setMode(mode === "login" ? "signup" : "login")}
            className="w-full font-mono text-[11px] text-zinc-500 hover:text-signal-yellow tracking-[0.18em] uppercase transition-colors"
          >
            {mode === "login" ? "No account? Create one →" : "Already operating? Sign in →"}
          </button>
        </form>
      </div>
    </div>
  );
}
